import LogInMessage from "@iewcasia/Components/LogInMessage";
import { getPdf } from "@iewcasia/Services/OrderService";
import { setGA4Event } from "@iewcasia/Store/Handlers/Utilities/CustomTracking";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        pageTitle: getCurrentPage(state).fields.title,
        IsAuthenticated: state.context.session.isAuthenticated,
    };
};

const DOWNLOAD_ICON = (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="15">
        <path
            fill="#E82011"
            fillRule="nonzero"
            d="M6.28 10.885c.076.075.179.115.279.115.1 0 .203-.037.28-.115L10 7.795a.38.38 0 0 0 0-.549.403.403 0 0 0-.562 0L6.956 9.672V.388A.391.391 0 0 0 6.559 0a.391.391 0 0 0-.397.388v9.284L3.679 7.246a.403.403 0 0 0-.562 0 .38.38 0 0 0 0 .55l3.162 3.089ZM12.555 14H.444c-.247 0-.444.17-.444.382 0 .213.197.383.444.383h12.112c.247 0 .444-.17.444-.383 0-.212-.197-.382-.444-.382Z"
        />
    </svg>
);

interface OrderDocumentDownloadStyles {
    download?: InjectableCss;
    overlay?: InjectableCss;
    toast?: InjectableCss;
    loader?: InjectableCss;
    close?: InjectableCss;
    error?: TypographyPresentationProps;
}

interface OwnProps {
    id: string;
    type: string;
    name: string;
}

export const styles: OrderDocumentDownloadStyles = {
    download: {
        css: css`
            display: flex;
            align-items: center;
            gap: 5px;
            padding: 0;
            min-height: 0;
            border: 0;
        `,
    },
    overlay: {
        css: css`
            position: fixed;
            top: 0;
            left: 0;
            z-index: 1000;
            width: 100%;
            height: 100%;
            background: rgba(255, 255, 255, 0.5);
            display: flex;
            padding: 0 30px 50px 0;
            align-items: flex-end;
            justify-content: flex-end;
        `,
    },
    toast: {
        css: css`
            width: 300px;
            height: 45px;
            background: #fff;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            gap: 10px;
            box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.17);
            border: solid 1px #d2d2d2;
            margin-top: auto;
            padding-left: 5px;
            border-left: solid 3px #4485f4;

            .error & {
                border-left-color: #e82011;
            }
        `,
    },
    loader: {
        css: css`
            font-size: 4px;
            position: relative;
            text-indent: -9999em;
            transform: translateZ(0);
            animation: load8 0.5s infinite linear;
            z-index: 10;
            width: 20px;
            height: 20px;
            border-top: 2.5px solid #ebebeb;
            border-right: 2.5px solid #ebebeb;
            border-bottom: 2.5px solid #ebebeb;
            border-left: 2.5px solid dodgerblue;
            border-radius: 50%;

            @keyframes load8 {
                0% {
                    -webkit-transform: rotate(0deg);
                    transform: rotate(0deg);
                }
                100% {
                    -webkit-transform: rotate(360deg);
                    transform: rotate(360deg);
                }
            }
        `,
    },
    error: {
        size: 12,
        color: "#E82011",
        css: css`
            white-space: break-spaces;
        `,
    },
    close: {
        css: css`
            color: #4a4a4a;
            border: 0;
            background: 0;
            align-self: flex-start;
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const OrderDocumentDownload = ({ id, type, name, pageTitle, IsAuthenticated }: Props) => {
    const [loading, setLoading] = useState(false);
    const [isErrored, setIsErrored] = useState(false);

    const btnLabel = translate("Download");

    const download = async () => {
        setLoading(true);
        try {
            const { results } = (await getPdf({ documentId: id, type })) as any;
            const base64String = results.pdF_B64STR;
            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });

            setLoading(false);

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${name}.pdf`;

            const currentLocation = window.location;

            setGA4Event([], "sap_file_download", {
                file_name: name,
                file_extension: link.download.split(".").pop(),
                link_text: btnLabel,
                link_url: currentLocation.href,
                pageTitle,
            });

            link.click();
        } catch {
            setLoading(false);
            setIsErrored(true);
        }
    };

    const message = translate("Log In to download");

    return (
        <>
           {IsAuthenticated && (
            <Button variant="tertiary" {...styles.download} onClick={download}>
                {DOWNLOAD_ICON}
                {btnLabel}
            </Button>
           )}
           {!IsAuthenticated && (
            <LogInMessage title={message}  />
           )}
            
            {loading && (
                <StyledWrapper {...styles.overlay}>
                    <StyledWrapper {...styles.toast}>
                        <StyledWrapper {...styles.loader} />
                        <Typography size={14}>{translate("Preparing your document for download...")}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {isErrored && (
                <StyledWrapper {...styles.overlay} className="error" onClick={() => setIsErrored(false)}>
                    <StyledWrapper {...styles.toast}>
                        <Typography {...styles.error}>
                            {translate(
                                "We're sorry, this document is not currently available. Please try again later.",
                            )}
                        </Typography>
                        <StyledWrapper as="button" {...styles.close}>
                            x
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps)(OrderDocumentDownload as any) as any;
